import axios from 'axios';
import { API_BASE_URL } from '../config';

const BASE = API_BASE_URL;

/**
 * Check delivery serviceability and get estimated delivery message.
 * @param {string} pincode - 6-digit pincode
 * @returns {Promise<{ serviceable: boolean, message?: string, estimatedDays?: number }>}
 */
export async function getDeliveryEstimate(pincode) {
    const p = (pincode || '').toString().trim();
    if (p.length !== 6) {
        return { serviceable: false, message: 'Please enter a valid 6-digit pincode.' };
    }
    try {
        const { data } = await axios.get(`${BASE}/delivery/estimate`, {
            params: { pincode: p },
        });
        const payload = data?.data;
        if (data?.result === 0 || !payload) {
            return {
                serviceable: false,
                message: payload?.message || data?.msg || "We don't deliver to this pincode yet.",
            };
        }
        return {
            serviceable: !!payload.serviceable,
            message: payload.message || null,
            estimatedDays: payload.estimatedDays ?? null,
        };
    } catch (err) {
        console.error('Delivery estimate error:', err);
        const payload = err.response?.data?.data;
        return {
            serviceable: false,
            message:
                payload?.message ||
                err.response?.data?.msg ||
                'Unable to check delivery. Try again.',
        };
    }
}

/**
 * Get user's saved pincode (logged-in only).
 * @returns {Promise<{ pincode: string | null }>}
 */
export async function getUserPincode() {
    const token = localStorage.getItem('token');
    if (!token) return { pincode: null };
    try {
        const { data } = await axios.get(`${BASE}/users/pincode`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const payload = data?.data;
        return {
            pincode: payload?.pincode != null ? String(payload.pincode) : null,
        };
    } catch (err) {
        console.error('Get user pincode error:', err);
        return { pincode: null };
    }
}

/**
 * Save pincode to user profile (logged-in only).
 * @param {string} pincode - 6-digit pincode
 * @returns {Promise<boolean>} - true if saved
 */
export async function setUserPincode(pincode) {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const p = (pincode || '').toString().trim();
    if (p.length !== 6) return false;
    try {
        await axios.post(
            `${BASE}/users/pincode`,
            { pincode: p },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return true;
    } catch (err) {
        console.error('Set user pincode error:', err);
        return false;
    }
}

/**
 * Live Shiprocket tracking for an order (uses AWB stored after manual shipment).
 * @param {string} orderId
 */
export async function getOrderTracking(orderId) {
    if (!orderId) {
        return { success: false, available: false, message: 'Order not found.' };
    }
    try {
        const { data } = await axios.get(`/orders/${orderId}/tracking`);
        return data;
    } catch (err) {
        console.error('Order tracking error:', err);
        return {
            success: false,
            available: false,
            message: err.response?.data?.message || 'Unable to load tracking.',
        };
    }
}
